import { colors2 } from "oolib";

/**
 * Static chart config — the chart-poc DialKit panel flattened into one frozen
 * object. Dataset-agnostic defaults; DrilldownDonut callers override individual
 * values via its `config` prop.
 *
 * Geometry values are in scene px of the fixed logical viewport (see
 * layout.js); label sizes are in *screen* px, held constant however the
 * chart is scaled.
 */
export const CHART_CONFIG = {
  // Rings
  /** Centre-line radius of ring 1, in scene px. */
  baseRadius: 180,
  /** Added to the base radius for every open level, so the whole chart can
   *  pull inward (negative) as it drills down. Animates with the reveal. */
  drillShrink: 0,
  /** Empty space between a ring's outer edge and its child ring's inner edge. */
  ringOffset: 40,
  ringThickness: 32,
  cornerRadius: 4,
  /** Gap between neighbouring segments, in px, at every ring. */
  segmentGap: 3,
  /** Where the first segment begins, in degrees clockwise from 12 o'clock. */
  startAngle: 0,
  /** How many rings deep the chart can drill. Fallback only — DrilldownDonut
   *  overrides it with the actual depth of the tree it's given. */
  ringCount: 4,

  // Zoom — the camera move toward the point of interest while drilling.
  zoomEnabled: true,
  /** Padding around the point-of-interest square, in scene px. Also the
   *  breathing room labels get outside the framed ring, so it stays generous. */
  zoomBuffer: 340,

  // Motion
  /** Thickness multiplier for the hovered segment. */
  hoverScale: 1.4,
  /** Thickness multiplier for the selected segment. */
  selectedScale: 1.4,
  /** Duration (s) and cubic-bezier curve for every reveal, hover and fade. */
  transition: { duration: 0.35, ease: [0, 0.6, 0, 1] },

  // Labels — all labels bend on the guide ring of the outermost revealed
  // ring (the POC's "extending-arm" variant), so leader arms lengthen as
  // levels open instead of getting overdrawn by them.
  /** Label size in screen px, held constant however the chart is scaled. */
  labelSize: 9,
  labelWeight: 600,
  /** The count prefix, set heavier than the label so the number reads first.
   *  DM Sans is loaded as a variable font (100..1000), so 900 is a real
   *  weight here rather than a synthesised fallback. */
  labelCountWeight: 900,
  /** Letter spacing, as a fraction of the label size. */
  labelTracking: 0.1,
  /** How far the text sits above its rule, as a fraction of the label size. */
  labelBaseline: 0.35,
  /** Prefix every label with what its segment weighs: "12 DROP OFF". Applies
   *  to leader labels and the on-band curved names alike. */
  showValues: true,
  /** Widest a label line may run, in multiples of the label size; longer
   *  text word-wraps onto the next line. */
  labelMaxWidthEm: 36,
  /** Lines a label may wrap to before the last one gets an ellipsis. */
  labelMaxLines: 1,
  /** Line height, as a fraction of the label size. */
  labelLineHeight: 1.3,
  /** Gap between a segment's outer edge and its leader's anchor. */
  labelStartOffset: 8,
  /** Guide-ring radius, measured out from the outermost revealed ring's edge. */
  labelGuideOffset: 80,
  /** Shortest the horizontal arm is allowed to be. */
  labelArmMin: 70,
  /** Rule left bare beyond the text, when the text is what sets the length. */
  labelArmPadding: 24,
  leaderWidth: 1,

  // Track — the dotted circle marking an opened ring
  trackWidth: 1.5,
  trackDash: 2,
  trackGap: 7,

  // Hatch — the fill on unselected siblings of a drilled segment
  hatchBg: "#FFFFFF",
  hatchLine: "#DCDFE4",
  hatchBorder: "#C8CED6",
  hatchTile: 10,

  // Colours
  colorActiveLabel: colors2.grey80,
  colorMutedLabel: colors2.grey40,
  colorLeader: "#C7CBD1",
  /** Fill of the segment you drilled into, at every level. */
  colorSelected: "#9AA1AB",
  /** The drilled segment's on-arc label (its leader label hands over to its
   *  children, so the name curves along the selected band instead). */
  colorArcLabel: "#FFFFFF",
  colorTrack: "#C8CED6",
};
